import type { CurrentUser } from '../data/users/CurrentUser';

/**
 * Onboarding step skip status
 */
export interface OnboardingSkipStatus {
  /** Skip if firstName AND lastName exist */
  skipNameStep: boolean;
  /** Skip if displayJobTitle OR currentRoleStandardLibraryJobDefinitionId OR (hasJob && jobTitle) */
  skipRoleStep: boolean;
  /** Skip if statedOrganizationName exists, or the user belongs to a real org (not My Klayo) */
  skipOrganizationStep: boolean;
  /** Skip if the user already has a stated country. Country (and the region we infer from it) is a
   *  mandatory LearnUpon field, so this step is asked independently of the organization step — a user
   *  invited into a real org skips the org step but must still be asked for their country. */
  skipCountryStep: boolean;
}

/**
 * Determine which onboarding steps should be skipped based on user data.
 *
 * Every field here is read off the ACTIVE user — the impersonated employee when an admin is viewing
 * someone else, not the logged-in admin. CurrentUser carries both sets (`firstName` vs
 * `loggedInUserFirstName`), and they are different values.
 *
 * Deliberately reports only which steps are answerable. Whether onboarding applies to this user at
 * all is needsOnboarding's job — see the note there.
 */
export function getOnboardingSkipStatus(user: CurrentUser | undefined): OnboardingSkipStatus {
  if (!user) {
    return {
      skipNameStep: false,
      skipRoleStep: false,
      skipOrganizationStep: false,
      skipCountryStep: false,
    };
  }

  // Name step: Skip if both first and last name exist
  const skipNameStep = !!(user.firstName && user.lastName);

  // Role step: Skip if displayJobTitle OR currentRoleStandardLibraryJobDefinitionId OR (hasJob && jobTitle)
  const skipRoleStep = !!(
    user.displayJobTitle ||
    user.currentRoleStandardLibraryJobDefinitionId ||
    (user.hasJob && user.jobTitle)
  );

  // Organization step: only relevant for My Klayo self-signups who state their own organization.
  // Skip if they already stated one, or they belong to a real org (e.g. synced from LearnUpon).
  const skipOrganizationStep = !!user.statedOrganizationName || user.isMyKlayoUser === false;

  // Country step: skip if we already know the user's country. Asked independently of the org step so
  // real-org invited users (who skip the org step) are still prompted for their country.
  const skipCountryStep = !!user.statedCountry;

  return {
    skipNameStep,
    skipRoleStep,
    skipOrganizationStep,
    skipCountryStep,
  };
}

/**
 * Single source of truth for "onboarding applies to this user". Every routing rule and the
 * Onboarding page itself must agree, or they fight each other.
 *
 * They did fight, in production: an org admin impersonating an employee who had never onboarded got
 * an infinite /onboarding -> / -> /onboarding loop that rendered a blank page, because two redirect
 * rules read onboardingCompletedDate directly while only the Onboarding page knew impersonation was
 * special. That is why completeness is not exposed on OnboardingSkipStatus — reaching it without
 * this check is what caused the outage.
 *
 * False while impersonating because onboarding cannot be completed on someone else's behalf: the
 * POST activates the viewed employee, stamps their OnboardingCompletedDate and provisions them into
 * LearnUpon. Note this only stops the automatic path — an impersonating admin who navigates to
 * /onboarding with steps still unanswered is shown the form, and submitting it writes the viewed
 * employee. Closing that properly needs a server-side check on POST /Employee/Onboarding.
 */
export function needsOnboarding(user: CurrentUser | undefined): boolean {
  if (!user) return false;
  if (user.impersonating) return false;

  return !user.onboardingCompletedDate;
}
